import { ISlot } from 'constants/types';
import { useQuery } from 'hooks/useQuery';
import { apiGet } from 'lib/api/client';
import {
  getSlotById,
  listSlots,
  listSlotsByCasino,
  listSlotsByProvider,
} from 'lib/api/slots';
import { PaginationResponse } from 'lib/api/types';
import { URLS } from 'api/constants';
import { HookQueryOptions, queryKey } from './shared';

export type SlotSorting = 'bigwins-asc' | 'bigwins-des' | null | undefined;

interface SlotsRequest {
  ids?: string[];
  page?: number;
  limit?: number;
  sort?: SlotSorting;
  collections?: string;
  countries?: string | null;
}

export const useSlotsQuery = (
  payload: SlotsRequest = {},
  options?: HookQueryOptions
) =>
  useQuery(
    queryKey('slots.list', payload),
    () => {
      if (payload.ids?.length) {
        return apiGet<PaginationResponse<ISlot>>(
          `${URLS.SLOTS}${payload.ids.join(',')}`
        );
      }

      return listSlots({
        page: payload.page,
        limit: payload.limit,
        countries: payload.countries,
        collections: payload.collections,
        sortBy: payload.sort ?? undefined,
      });
    },
    { skip: options?.skip }
  );

export const useSlotByIdQuery = (
  payload: { id: string },
  options?: HookQueryOptions
) =>
  useQuery(
    queryKey('slots.byId', payload),
    async () => {
      const data = await getSlotById(payload.id);
      const response = data as unknown as PaginationResponse<ISlot> | ISlot;
      if (
        typeof response === 'object' &&
        response !== null &&
        'results' in response &&
        Array.isArray(response.results)
      ) {
        return response.results[0] as ISlot;
      }
      return response as ISlot;
    },
    { skip: options?.skip || !payload?.id }
  );

export const useSlotsByCasinoIdQuery = (
  payload: { id: string },
  options?: HookQueryOptions
) =>
  useQuery(
    queryKey('slots.byCasinoId', payload),
    () => listSlotsByCasino(payload.id),
    { skip: options?.skip || !payload?.id }
  );

export const useSlotsByProviderIdQuery = (
  payload: { id: string },
  options?: HookQueryOptions
) =>
  useQuery(
    queryKey('slots.byProviderId', payload),
    () => listSlotsByProvider(payload.id),
    { skip: options?.skip || !payload?.id }
  );
